import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { ChatAPI } from '../utils/api';

type SummaryType = 'ccr' | 'authorization';

interface SummaryPdfViewerProps {
  summaryType: SummaryType;
}

export const SummaryPdfViewer: React.FC<SummaryPdfViewerProps> = ({ summaryType }) => {
  const { summaryId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const generatedAt = searchParams.get('generated_at') ?? undefined;
  const complaintId = searchParams.get('complaint_id') ?? undefined;
  const { isAuthenticated, user, getToken } = useAuth();
  // Child effects run before AuthProvider re-registers ChatAPI's token provider, so request the token directly.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setPdfUrl(null);
    setError(null);

    const loadPdf = async () => {
      try {
        const accessToken = await getTokenRef.current();
        if (!accessToken) {
          throw new Error('Unable to get an access token. Please sign in again.');
        }
        const pdf = summaryType === 'ccr'
          ? await ChatAPI.getCcrSummaryPdf(summaryId, generatedAt, complaintId, accessToken)
          : await ChatAPI.getAuthorizationSummaryPdf(summaryId, generatedAt, accessToken);
        objectUrl = URL.createObjectURL(pdf);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPdfUrl(objectUrl);
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : 'Unable to open the summary PDF.');
        }
      }
    };

    void loadPdf();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [summaryId, generatedAt, complaintId, summaryType, isAuthenticated, user]);

  const title = summaryType === 'ccr' ? 'CCR Summary PDF' : 'Authorization Summary PDF';

  return (
    <section style={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0, padding: 16 }}>
      <h1>{title}</h1>
      <p><Link to="/">Return to Aurora</Link></p>
      {error && <p role="alert">{error}</p>}
      {!error && !pdfUrl && <p role="status">Loading summary PDF...</p>}
      {pdfUrl && (
        <iframe
          title={title}
          src={pdfUrl}
          style={{ border: 0, flex: 1, minHeight: 0, width: '100%' }}
        />
      )}
    </section>
  );
};